import Phaser from "phaser";
import { defenseMultiplayer } from "../systems/multiplayer-client";
import { normalizeLobbyCode, type MultiplayerMode } from "../systems/multiplayer-protocol";
import { addButton, addCastleBackdrop, COLORS, title } from "./shared";

function textValue(value: unknown, fallback: string): string {
  return typeof value === "string" || typeof value === "number" ? String(value) : fallback;
}

function numberValue(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

export class MultiplayerLobbyScene extends Phaser.Scene {
  private unsubscribe: (() => void) | undefined;
  private statusText?: Phaser.GameObjects.Text;
  private lobbyText?: Phaser.GameObjects.Text;
  private selectedMode: MultiplayerMode = "coop";
  private enteringMatch = false;

  constructor() {
    super("MultiplayerLobby");
  }

  create(): void {
    addCastleBackdrop(this);
    title(this, "MULTIPLAYER MUSTER", "Create a private lobby or join with a royal code");
    const center = this.scale.width / 2;
    const compact = this.scale.width < 720;
    const panelWidth = Math.min(compact ? this.scale.width - 24 : 650, 650);
    const panelHeight = Math.min(this.scale.height - 220, 500);
    this.add
      .rectangle(center, 190 + panelHeight / 2, panelWidth, panelHeight, COLORS.panel, 0.96)
      .setStrokeStyle(3, COLORS.goldDark);

    this.statusText = this.add
      .text(center, 225, "Preparing LittleCastle realtime…", {
        fontFamily: "Trebuchet MS, sans-serif",
        fontSize: "16px",
        color: "#fff3cf",
        align: "center",
        wordWrap: { width: panelWidth - 50 },
      })
      .setOrigin(0.5);
    this.lobbyText = this.add
      .text(center, 285, "No lobby joined.", {
        fontFamily: "Trebuchet MS, sans-serif",
        fontSize: "14px",
        color: "#e8cf91",
        align: "center",
        lineSpacing: 7,
        wordWrap: { width: panelWidth - 50 },
      })
      .setOrigin(0.5, 0);

    const y = Math.min(this.scale.height - 225, 505);
    addButton(
      this,
      center - 145,
      y,
      260,
      "Create Co-op Lobby",
      () => {
        this.selectedMode = "coop";
        void this.createLobby();
      },
      true,
    );
    addButton(this, center + 145, y, 260, "Create Head-to-Head", () => {
      this.selectedMode = "head-to-head";
      void this.createLobby();
    });
    addButton(this, center - 145, y + 62, 260, "Join by Code", () => this.promptForCode());
    addButton(this, center + 145, y + 62, 260, "Ready / Not Ready", () => this.toggleReady());
    addButton(this, center - 145, y + 124, 260, "Start Match", () => this.startMatch());
    addButton(this, center + 145, y + 124, 260, "Leave Lobby", () => this.leaveLobby());
    addButton(this, center, y + 186, 260, "Back", () => this.scene.start("MainMenu"));

    this.unsubscribe = defenseMultiplayer.subscribe(() => {
      this.renderState();
      if (defenseMultiplayer.matchSnapshot && this.scene.isActive() && !this.enteringMatch) {
        this.enteringMatch = true;
        this.scene.start("CoopBattle");
      }
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribe?.();
      this.unsubscribe = undefined;
    });
    this.renderState();
    void defenseMultiplayer.connect().catch(() => this.renderState());
  }

  private async createLobby(): Promise<void> {
    try {
      await defenseMultiplayer.connect();
      defenseMultiplayer.send({
        type: "lobby:create",
        mode: this.selectedMode,
        visibility: "private",
      });
    } catch {
      this.renderState();
    }
  }

  private promptForCode(): void {
    const raw = window.prompt("Enter the lobby code:", "");
    if (raw === null) return;
    const code = normalizeLobbyCode(raw);
    if (code.length < 4) {
      this.statusText?.setText("Lobby codes must contain at least four letters or numbers.");
      return;
    }
    try {
      defenseMultiplayer.send({ type: "lobby:join", code });
    } catch {
      this.renderState();
    }
  }

  private toggleReady(): void {
    const lobby = defenseMultiplayer.lobbyState;
    if (!lobby) return;
    const local =
      lobby.players.find((player) => player.playerId === defenseMultiplayer.playerId) ??
      lobby.players[0];
    try {
      defenseMultiplayer.send({ type: "lobby:ready", ready: !(local?.ready ?? false) });
    } catch {
      this.renderState();
    }
  }

  private startMatch(): void {
    try {
      defenseMultiplayer.send({ type: "match:start" });
    } catch {
      this.renderState();
    }
  }

  private leaveLobby(): void {
    try {
      if (defenseMultiplayer.lobbyState) defenseMultiplayer.send({ type: "lobby:leave" });
    } catch {
      // The connection may already be gone; returning to the menu is still safe.
    }
    this.scene.start("MainMenu");
  }

  private renderState(): void {
    if (!this.statusText || !this.lobbyText) return;
    const diagnostic = defenseMultiplayer.diagnostic();
    const labels = {
      idle: "Realtime idle",
      authenticating: "Verifying LittleCastle account…",
      connecting: "Connecting to the Citadel realtime service…",
      connected: "Connected to LittleCastle realtime",
      reconnecting: `Reconnecting… attempt ${diagnostic.reconnectAttempts}`,
      disconnected: "Disconnected",
      error: diagnostic.lastError ?? "Multiplayer connection error",
    } as const;
    this.statusText.setText(labels[diagnostic.state]);
    const match = defenseMultiplayer.matchSnapshot;
    if (match) {
      const payload = match.payload;
      const players = Array.isArray(payload.players) ? payload.players : [];
      const health = Number(payload.sharedCastleHealth ?? 0);
      const maxHealth = Number(payload.sharedCastleMaxHealth ?? 100);
      this.lobbyText.setText(
        `CO-OP MATCH ACTIVE • Wave ${numberValue(payload.wave, 1)}\n` +
          `Shared Castle: ${health}/${maxHealth} • Score ${numberValue(payload.sharedScore, 0)}\n\n` +
          players
            .map((entry) => {
              const player =
                typeof entry === "object" && entry !== null
                  ? (entry as Record<string, unknown>)
                  : {};
              const towers = Array.isArray(player.towers) ? player.towers.length : 0;
              return `${textValue(player.displayName, "Player")} • ${numberValue(player.gold, 0)} gold • ${towers} towers`;
            })
            .join("\n"),
      );
      return;
    }
    const lobby = defenseMultiplayer.lobbyState;
    if (!lobby) {
      this.lobbyText.setText(
        "No lobby joined.\n\nPhase 37 provides authenticated transport, protocol versioning, lobby commands, reconnect, and authoritative snapshot handling.",
      );
      return;
    }
    const players = lobby.players
      .map(
        (player) =>
          `${player.ready ? "✓" : "○"} ${player.displayName}${player.connected ? "" : " (reconnecting)"}`,
      )
      .join("\n");
    this.lobbyText.setText(
      `Lobby ${lobby.code} • ${lobby.mode === "coop" ? "Co-op" : "Head-to-Head"}\n` +
        `Status: ${lobby.status} • Revision ${lobby.revision}\n\n${players}`,
    );
  }
}
